import * as THREE from "three";

import Sizes from "./Utils/Sizes";
import Time from "./Utils/Time";
import SceneData from "./Utils/SceneData";
import Camera from "./Camera";
import Renderer from "./Renderer";
import Css3dRenderer from "./Css3dRenderer";
import World from "./World/World";
import Resources from "./Utils/Resources";
import Debug from "./Utils/Debug";

let instance = null;

export default class Experience {
  constructor(canvas){
    // Guard BEFORE window.experience is touched — a second `new
    // Experience()` call (e.g. a Vite HMR reload re-running script.js
    // while the first instance's async init() is still in flight) must
    // return the original instance untouched. Setting window.experience
    // unconditionally here would overwrite it with this second, empty,
    // never-initialized object even though the constructor itself
    // correctly returns the real instance — any code reading
    // window.experience afterward would see the wrong one.
    if(instance){
      return instance;
    }
    instance = this;
    window.experience = this;

    this.canvas = canvas;

    this.sizes = new Sizes();
    this.time = new Time();
    this.scene = new THREE.Scene();
    this.resources = new Resources();
    this.sceneData = new SceneData();
    // Dev-only FPS overlay — never constructed in a production build.
    this.debug = import.meta.env.DEV ? new Debug() : null;

    // Single shared timeline position, in baked-camera frames (0-360).
    // Camera/SequenceBG/CouplesRotation all read the same value each tick
    // instead of three separately-advancing counters that could drift.
    // World's scroll listeners are what actually move it.
    this.playhead = 0;

    // Camera/CameraPath read scene.json's baked frames at construction time
    // (precomputing the 361-frame Vector3 array), so nothing that depends on
    // sceneData can construct until its fetch resolves. World/Renderer wait
    // on the same promise via this.ready.
    this.ready = this.init();
  }

  async init(){
    await this.sceneData.load();

    this.camera = new Camera();
    // Css3dRenderer before World: VideoTextDom (constructed inside World)
    // reads this.css3dRenderer.scene to add its CSS3DObject.
    this.css3dRenderer = new Css3dRenderer();
    // World before Renderer: SequenceBG (constructed inside World) owns the
    // plate's bgScene/bgCamera, which Renderer's TwoScenePass needs to
    // exist at construction time.
    this.world = new World();
    this.renderer = new Renderer();

    // TextCard's font-driven canvas texture and CouplesRotation's atlas
    // (the atlas especially — 46 frames packed into one up-to-~4096px-
    // square CanvasTexture) both finish setting up asynchronously, in the
    // background, well before either card is first shown (its own mesh
    // stays invisible the whole time). Without this, WebGLRenderer defers
    // their shader program link AND their texture's GPU upload until the
    // first actual draw call — which landed on the exact tick
    // showCouplesRotation() and the text fade-in both flip visible (same
    // playhead moment), producing one very expensive frame (~700-800ms,
    // confirmed via the debug overlay and reproduced headlessly). Awaiting
    // both here and pre-compiling + pre-uploading once, before the tick
    // loop starts, moves that one-time cost to load time instead.
    await Promise.all([this.world.textCard.ready, this.world.couplesRotation.ready]);
    this.renderer.warmup([
      this.world.textCard.texture,
      this.world.couplesRotation.atlasTexture,
    ]);

    this.sizes.on("resize", () => {
      this.resize();
    });

    this.time.on("tick", () => {
      this.update();
    });
  }

  resize(){
    this.camera.resize();
    this.renderer.resize();
    this.css3dRenderer.resize();
    this.world.resize();

    this.renderer.update();
  }

  update(){
    const dt = Math.min(this.time.delta / 1000, 0.1);

    // playhead must be advanced before camera reads it this tick, or the
    // camera lags one tick behind where BG/turntable end up.
    this.world.updatePlayhead();

    this.debug?.begin("camera");
    this.camera.update(dt, this.playhead);
    this.debug?.end("camera");

    this.debug?.begin("world");
    this.world.update(dt);
    this.debug?.end("world");

    this.debug?.begin("render");
    this.renderer.update();
    this.debug?.end("render");

    this.debug?.begin("render:css3d");
    this.css3dRenderer.update();
    this.debug?.end("render:css3d");

    this.debug?.update(this.renderer.instance.info);
  }
}